import { useCallback, useState } from "react";
import type { Slag } from "./dokumenter";

/**
 * Kontor, innhold, opptak og teknisk.
 *
 * ET FOKUSVALG, IKKE EN TILGANG. Alle kan slå dem på og av når som helst, og
 * ingenting er skjult for noen — det følger av den ufravikelige regelen:
 * «Alle medlemmer av en produksjon leser alt som ligger i den. Rolle styrer
 * skriving og oppsett, ikke lesing.»
 *
 * Det området løser er noe annet: en A-foto som rigger skal ikke bla forbi
 * dagsplanen og kjøreplanen for å komme til kameraplanen.
 *
 * FIRE BRYTERE, IKKE ETT VALG. Man kan være to ting samtidig — en fotograf
 * som også skriver nedbrekk — og da slår man på begge. Er ingen slått på,
 * vises alt: en tom flate er aldri svaret på at man har klikket feil.
 *
 * Valget bor i nettleseren, fordi det er en vane hos den enkelte. Når auth og
 * medlemslister finnes, kan prodleder sette hva en person STARTER med — men
 * bryterne skal fortsatt være frie.
 */

export type Omraade = "kontor" | "innhold" | "opptak" | "teknisk";

export interface Omraadedef {
  readonly id: Omraade;
  readonly navn: string;
  readonly sider: readonly string[];
  readonly briefFelt: readonly string[];
  readonly bokser: readonly Slag[];
}

export const OMRAADER: readonly Omraadedef[] = [
  {
    id: "kontor",
    navn: "Kontor",
    sider: ["brief", "opplasting"],
    briefFelt: ["brief-prod"],
    bokser: ["dagsplan", "kjoreplan"],
  },
  {
    id: "innhold",
    navn: "Innhold",
    /* «deltakere» sto her til 02.11.2026. Fanen er borte fra
       arbeidsflata — lista ligger under Oppsett · Innhold. Se App.tsx. */
    sider: [
      "spoersmaal", "brief", "shotlist", "opplasting", "rapporter",
      "logg", "synk", "livelogg",
    ],
    briefFelt: ["brief-innhold"],
    bokser: ["dagsmanus", "nedbrekk"],
  },
  /*
   * OPPTAK er crewet som står i det mens det skjer.
   *
   * Lagt til 24.10.2026, med reporterens synkflate. Det som skiller den fra
   * de tre andre er at dette ikke er administrativt arbeid: kontoret,
   * redaktøren og A-foto setter opp og leser, reporteren GJØR noe mens
   * kameraet går. Flatene generelt crew bruker hører hit.
   */
  {
    id: "opptak",
    navn: "Opptak",
    sider: ["livelogg", "synk", "brief", "opplasting"],
    /* Synkspørsmålene er redaksjonelt innhold, og briefen om dem er den
       reporteren trenger. */
    briefFelt: ["brief-innhold"],
    bokser: ["dagsmanus", "dagsplan"],
  },
  {
    id: "teknisk",
    navn: "Teknisk",
    /* «kameraplan» sto her til 02.11.2026. Tegneflata ligger naa under
       Oppsett · Kameraplan — A-foto rigger med den, man blar ikke forbi
       den paa en opptaksdag. Se App.tsx. */
    sider: ["brief", "shotlist", "utstyr", "opplasting"],
    briefFelt: ["brief-teknisk"],
    bokser: ["kameraplan"],
  },
];

/** Det som vises. Tomme lister betyr ALT — ingenting er filtrert bort. */
export interface Valgt {
  readonly paa: ReadonlySet<Omraade>;
  readonly sider: readonly string[];
  readonly briefFelt: readonly string[];
  readonly bokser: readonly Slag[];
}

function slaaSammen(paa: ReadonlySet<Omraade>): Valgt {
  /* Ingen brytere på: alt vises. Se forklaringen øverst. */
  if (paa.size === 0) return { paa, sider: [], briefFelt: [], bokser: [] };
  const med = OMRAADER.filter((o) => paa.has(o.id));
  return {
    paa,
    sider: [...new Set(med.flatMap((o) => o.sider))],
    briefFelt: [...new Set(med.flatMap((o) => o.briefFelt))],
    bokser: [...new Set(med.flatMap((o) => o.bokser))],
  };
}

const NOKKEL = "infoflyt.omraader.v1";

function les(): Set<Omraade> {
  try {
    const r = localStorage.getItem(NOKKEL);
    if (r === null) return new Set();
    const liste = JSON.parse(r) as unknown;
    if (!Array.isArray(liste)) return new Set();
    return new Set(liste.filter((x): x is Omraade =>
      OMRAADER.some((o) => o.id === x)));
  } catch {
    return new Set();
  }
}

export function useOmraader(): [Valgt, (o: Omraade, paa: boolean) => void] {
  const [paa, settPaa] = useState<ReadonlySet<Omraade>>(les);

  const veksle = useCallback((o: Omraade, skalPaa: boolean): void => {
    settPaa((f) => {
      const ny = new Set(f);
      if (skalPaa) ny.add(o); else ny.delete(o);
      try {
        localStorage.setItem(NOKKEL, JSON.stringify([...ny]));
      } catch {
        /* Privat modus kan nekte lagring. Da gjelder valget denne økta. */
      }
      return ny;
    });
  }, []);

  return [slaaSammen(paa), veksle];
}
